import React, { useState, useMemo } from 'react'
import { useTar } from '@/modules/i18n'
import { Button, Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, MarkdownEditor, Card, CardContent, IconTooltip } from '@duck/ui'
import { RelativeDateTime } from '../../datetime/RelativeDateTime'
import { GradeBadge } from '../../GradeBadge/GradeBadge'
import { WysiwygEditor } from '@components/WysiwygEditor'
import { ChallengeService } from '@duck/api-definitions'
import { getContentChartIds } from '@components/WysiwygEditor/wysiwygUtils'

interface FeedbackDetails {
    personName: string
    feedback: string
    startDate?: string
    submitDate?: string
    grade?: number
    solution?: string
    workTime?: number
    charts?: ChallengeService.WorkState['charts']
}

const formatWorkTime = (workTimeMinutes: number): string => {
    if (!workTimeMinutes) return '0m'
    const hours = Math.floor(workTimeMinutes / 60)
    const minutes = workTimeMinutes % 60
    if (hours > 0) {
        return `${hours}h ${minutes}m`
    }
    return `${minutes}m`
}

interface AssignmentSubmissionSummaryDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    feedbackDetails: FeedbackDetails | null
}

export const AssignmentSubmissionSummaryDialog: React.FC<AssignmentSubmissionSummaryDialogProps> = ({
    open,
    onOpenChange,
    feedbackDetails,
}) => {
    const { t } = useTar()
    const [showSolution, setShowSolution] = useState(false)

    React.useEffect(() => {
        if (!open) {
            setShowSolution(false)
        }
    }, [open])

    // Extract chart IDs from solution HTML and filter charts to only those in the solution
    const solutionCharts = useMemo(() => {
        if (!feedbackDetails?.solution || !feedbackDetails?.charts) {
            return {}
        }

        const tempDiv = document.createElement('div')
        tempDiv.innerHTML = feedbackDetails.solution
        const chartIds = getContentChartIds(tempDiv)

        // Filter charts to only include those that are in the solution
        const filteredCharts: ChallengeService.WorkState['charts'] = {}
        chartIds.forEach(chartId => {
            if (feedbackDetails.charts && feedbackDetails.charts[chartId]) {
                filteredCharts[chartId] = feedbackDetails.charts[chartId]
            }
        })

        return filteredCharts
    }, [feedbackDetails?.solution, feedbackDetails?.charts])

    if (!feedbackDetails) return null

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl overflow-hidden" noMinHeight>
                <DialogHeader>
                    <DialogTitle>
                        {showSolution ? t('challenges.submittedSolution') : t('challenges.submissionSummary')}
                    </DialogTitle>
                    <p className="text-sm text-muted-foreground">{feedbackDetails.personName}</p>
                </DialogHeader>
                <div className="space-y-6 overflow-y-auto pr-2 custom-scrollbar mb-4">
                    <Card>
                        <CardContent className="p-4">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                <div>
                                    <p className="text-xs text-muted-foreground mb-1">{t('common.started')}</p>
                                    {feedbackDetails.startDate ? (
                                        <RelativeDateTime
                                            isoDateString={feedbackDetails.startDate}
                                            className="text-sm font-medium"
                                        />
                                    ) : (
                                        <p className="text-sm text-muted-foreground">-</p>
                                    )}
                                </div>
                                <div>
                                    <p className="text-xs text-muted-foreground mb-1">{t('common.submitted')}</p>
                                    {feedbackDetails.submitDate ? (
                                        <RelativeDateTime
                                            isoDateString={feedbackDetails.submitDate}
                                            className="text-sm font-medium"
                                        />
                                    ) : (
                                        <p className="text-sm text-muted-foreground">-</p>
                                    )}
                                </div>
                                <div>
                                    <div className="text-xs text-muted-foreground mb-1 flex items-center gap-1 ">
                                        {t('common.actualWorkTime')}
                                        <IconTooltip content={t('common.actualWorkTimeTooltip')} />
                                    </div>
                                    <p className="text-sm font-medium">
                                        {feedbackDetails.workTime !== undefined ? formatWorkTime(feedbackDetails.workTime) : '-'}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-xs text-muted-foreground mb-1">{t('common.grade')}</p>
                                    <div className="flex items-center">
                                        {feedbackDetails.grade !== undefined ? (
                                            <GradeBadge grade={feedbackDetails.grade} small />
                                        ) : (
                                            <p className="text-sm text-muted-foreground">-</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    {showSolution ? (
                        feedbackDetails.solution && (
                            <div>
                                <div className="flex items-center justify-between mb-3">
                                    <h3 className="text-lg font-semibold text-foreground">{t('challenges.submittedSolution')}</h3>
                                    {feedbackDetails.feedback && (
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => setShowSolution(false)}
                                        >
                                            {t('challenges.viewFeedback')}
                                        </Button>
                                    )}
                                </div>
                                <div className="border border-border rounded-md p-4">
                                    <WysiwygEditor
                                        content={feedbackDetails.solution}
                                        startingCharts={solutionCharts}
                                        readOnly
                                    />
                                </div>
                            </div>
                        )
                    ) : (
                        feedbackDetails.feedback && (
                            <div className="">
                                <div className="flex items-center justify-between mb-3">
                                    <h3 className="text-lg font-semibold text-foreground">{t('challenges.aiFeedback')}</h3>
                                    {feedbackDetails.solution && (
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => setShowSolution(true)}
                                        >
                                            {t('challenges.viewSolution')}
                                        </Button>
                                    )}
                                </div>
                                <MarkdownEditor
                                    defaultValue={feedbackDetails.feedback}
                                    className="w-full h-full"
                                    mode="view"
                                />
                            </div>
                        )
                    )}
                </div>
                <DialogFooter>
                    <Button onClick={() => onOpenChange(false)}>
                        {t('common.close')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}

